import React, { useEffect, useState } from "react";
import { Alert, Avatar, Badge, Button, Card, Field, Input } from "../../design-system";
import { Icon, SupHeader, SupSection, SupSelect, CategoryIcon } from "./atoms";
import { PRIO, PRIO_ORDER } from "./data";
import { useSoporteNav } from "./nav";
import { useSupportMeta } from "./useMeta";
import useCurrentMembership from "../../helpers/useCurrentMembership";
import { getEmployees, createSupportTicket, getSupportAssets } from "../../helpers/kronaly_helper";

const unwrap = (r) => r?.data ?? r;
const empName = (e) => [e?.first_name, e?.last_name].filter(Boolean).join(" ").trim() || "Sin nombre";

/**
 * Soporte IT · Crear ticket — formulario guiado contra POST /support/tickets.
 * Usuario afectado = empleado real del workspace; categorías/técnicos del
 * catálogo configurable (meta). UI-only de inventario aplazado a su fase.
 */
function SoporteCrear() {
  document.title = "Crear ticket · Soporte IT | Kronaly";
  const nav = useSoporteNav();
  const { roots, subcategoriesByParent, technicians, meta } = useSupportMeta();
  const resources = meta?.resources || [];
  const { me, can, employeeProfileId } = useCurrentMembership();
  // El técnico (support:manage) puede abrir tickets a nombre de cualquiera; el
  // empleado normal solo a su nombre (el backend lo fuerza igualmente).
  const isTech = can("support:manage");

  const [employees, setEmployees] = useState([]);
  const [userId, setUserId] = useState("");
  const [cat, setCat] = useState("");
  const [sub, setSub] = useState("");
  const [serviceLabel, setServiceLabel] = useState("");
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [priority, setPriority] = useState("media");
  const [responsable, setResponsable] = useState("");
  const [assets, setAssets] = useState([]);
  const [assetId, setAssetId] = useState("");
  const [resourceId, setResourceId] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Solo el técnico necesita el listado de empleados (selector de afectado).
  // El empleado se autoselecciona y no puede cambiarlo.
  useEffect(() => {
    if (!isTech) { setUserId(employeeProfileId || ""); return; }
    let alive = true;
    getEmployees()
      .then((r) => { if (alive) setEmployees(unwrap(r) || []); })
      .catch(() => { if (alive) setEmployees([]); });
    return () => { alive = false; };
  }, [isTech, employeeProfileId]);

  // Equipos para vincular opcionalmente: el técnico ve los del afectado
  // seleccionado; el empleado ve los suyos (el backend autoescopa /support/assets).
  useEffect(() => {
    setAssetId("");
    if (isTech && !userId) { setAssets([]); return; }
    let alive = true;
    getSupportAssets(isTech ? { current_employee_profile_id: userId } : {})
      .then((r) => { if (alive) setAssets(unwrap(r) || []); })
      .catch(() => { if (alive) setAssets([]); });
    return () => { alive = false; };
  }, [isTech, userId]);

  const subs = cat ? subcategoriesByParent[cat] || [] : [];
  const canSubmit = userId && title.trim() && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const r = await createSupportTicket({
        title: title.trim(),
        description: desc.trim() || null,
        requester_employee_profile_id: userId,
        category_id: cat || null,
        subcategory_id: sub || null,
        service_label: serviceLabel.trim() || null,
        priority,
        assignee_membership_id: responsable || null,
        asset_id: assetId || null,
        resource_id: resourceId || null,
      });
      const ticket = unwrap(r);
      nav.go("ticket", { code: ticket.id });
    } catch (e) {
      setError(e?.message || String(e));
      setSaving(false);
    }
  };

  return (
    <div className="kr-page">
      <SupHeader
        onBack={() => nav.back()} backLabel="Volver a tickets"
        eyebrow="Soporte IT"
        title="Crear ticket"
        subtitle="¿Con qué necesitas ayuda? Empieza por el usuario y la categoría del problema."
      />

      {error && <Alert tone="danger" title="No se pudo crear el ticket">{error}</Alert>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 20, alignItems: "start" }} data-responsive-grid="2">
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Paso 1 */}
          <Card>
            <SupSection eyebrow="Paso 1" title="¿A quién afecta?" />
            <div style={{ marginTop: 16 }}>
              {isTech ? (
                <Field label="Usuario afectado" required>
                  <SupSelect value={userId} onChange={setUserId} placeholder="Selecciona un empleado…"
                    options={employees.map((e) => ({ value: e.id, label: e.position ? `${empName(e)} · ${e.position}` : empName(e) }))} />
                </Field>
              ) : (
                <Field label="Usuario afectado" help="El ticket se crea a tu nombre.">
                  <div style={{ display: "flex", alignItems: "center", gap: 10, height: 40, border: "1px solid var(--kr-border)", borderRadius: 6, background: "var(--kr-neutral-50)", padding: "0 12px" }}>
                    <Avatar name={me?.name || "?"} size={24} />
                    <span style={{ fontSize: 13, color: "var(--kr-fg-strong)", fontWeight: 500 }}>{me?.name || "Tú"}</span>
                  </div>
                </Field>
              )}
            </div>
          </Card>

          {/* Paso 2 */}
          <Card>
            <SupSection eyebrow="Paso 2" title="¿Qué tipo de problema es?" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 16 }} data-responsive-grid="3">
              {roots.map((c) => {
                const active = cat === c.id;
                return (
                  <button key={c.id} onClick={() => { setCat(c.id); setSub(""); }} style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "12px 14px",
                    border: `1px solid ${active ? "var(--kr-action)" : "var(--kr-border)"}`,
                    background: active ? "var(--kr-action-soft-bg)" : "#fff",
                    borderRadius: 10, cursor: "pointer", textAlign: "left",
                  }}>
                    <CategoryIcon icon={c.icon} color={c.color} size={32} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: "var(--kr-fg-strong)" }}>{c.name}</span>
                  </button>
                );
              })}
              {roots.length === 0 && (
                <div style={{ gridColumn: "1 / -1", fontSize: 12, color: "var(--kr-fg-muted)" }}>
                  No hay categorías configuradas todavía.
                </div>
              )}
            </div>
            <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <Field label="Subcategoría">
                <SupSelect value={sub} onChange={setSub} placeholder={cat ? "Selecciona…" : "Elige antes una categoría"} options={subs.map((s) => ({ value: s.id, label: s.name }))} />
              </Field>
              <Field label="Servicio / aplicación afectada" help="Outlook, ERP, Dropbox…">
                <Input value={serviceLabel} onChange={(e) => setServiceLabel(e.target.value)} placeholder="Opcional" />
              </Field>
            </div>
          </Card>

          {/* Paso 3 */}
          <Card>
            <SupSection eyebrow="Paso 3" title="Describe la incidencia" />
            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 16 }}>
              <Field label="Título del ticket" required>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej. Outlook no sincroniza el correo nuevo" />
              </Field>
              <Field label="Descripción" help="Qué ocurre, desde cuándo y cómo reproducirlo.">
                <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={4} placeholder="Detalla el problema…"
                  style={{ border: "1px solid var(--kr-border)", borderRadius: 6, padding: "10px 12px", fontSize: 13, fontFamily: "var(--kr-font-sans)", color: "var(--kr-fg-strong)", resize: "vertical", outline: "none", lineHeight: 1.5 }} />
              </Field>
              <div style={{ display: "grid", gridTemplateColumns: isTech ? "1fr 1fr" : "1fr", gap: 14 }}>
                <Field label="Prioridad">
                  <div style={{ display: "flex", gap: 6 }}>
                    {PRIO_ORDER.map((p) => (
                      <button key={p} onClick={() => setPriority(p)} style={{
                        flex: 1, height: 36, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer",
                        border: `1px solid ${priority === p ? "var(--kr-action)" : "var(--kr-border)"}`,
                        background: priority === p ? "var(--kr-action-soft-bg)" : "#fff",
                        color: priority === p ? "var(--kr-action-soft-fg)" : "var(--kr-fg-muted)",
                      }}>{PRIO[p].label}</button>
                    ))}
                  </div>
                </Field>
                {/* El responsable lo asigna el equipo técnico; el empleado no lo elige. */}
                {isTech && (
                  <Field label="Responsable" help="Opcional — puede asignarse luego">
                    <SupSelect value={responsable} onChange={setResponsable} placeholder="Sin asignar"
                      options={technicians.map((t) => ({ value: t.membership_id, label: t.name }))} />
                  </Field>
                )}
              </div>
            </div>
          </Card>

          {/* Paso 4 · opcional: equipo relacionado */}
          <Card>
            <SupSection eyebrow="Paso 4 · Opcional" title="¿Hay un equipo o servicio implicado?"
              right={<Badge tone="neutral">Solo si aplica</Badge>} />
            <p style={{ fontSize: 12, color: "var(--kr-fg-muted)", margin: "8px 0 14px" }}>
              Asocia un equipo físico o un recurso IT (software/servicio) solo para conservar su histórico de incidencias.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Field label="Equipo del usuario">
                {!userId ? (
                  <div style={{ fontSize: 12, color: "var(--kr-fg-subtle)" }}>Selecciona primero el usuario para ver sus equipos.</div>
                ) : assets.length === 0 ? (
                  <div style={{ fontSize: 12, color: "var(--kr-fg-subtle)" }}>Este usuario no tiene equipos asignados.</div>
                ) : (
                  <SupSelect value={assetId} onChange={setAssetId} placeholder="Sin equipo"
                    options={assets.map((a) => ({ value: a.id, label: `${a.code} · ${[a.brand, a.model].filter(Boolean).join(" ")}` }))} />
                )}
              </Field>
              <Field label="Recurso IT (software / servicio)">
                <SupSelect value={resourceId} onChange={setResourceId} placeholder="Sin recurso"
                  options={resources.map((r) => ({ value: r.id, label: r.name }))} />
              </Field>
            </div>
          </Card>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <Button variant="secondary" onClick={() => nav.back()}>Cancelar</Button>
            <Button variant="primary" icon="Check" disabled={!canSubmit} onClick={submit}>
              {saving ? "Creando…" : "Crear ticket"}
            </Button>
          </div>
        </div>

        {/* Resumen */}
        <div style={{ position: "sticky", top: 0 }}>
          <Card>
            <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 12 }}>Resumen</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 13 }}>
              <SummaryRow label="Usuario" value={userId ? empName(employees.find((e) => e.id === userId)) : "—"} />
              <SummaryRow label="Categoría" value={cat ? (roots.find((c) => c.id === cat)?.name || "—") : "—"} />
              <SummaryRow label="Prioridad" value={<Badge tone={PRIO[priority]?.tone || "neutral"} dot>{PRIO[priority]?.label}</Badge>} />
            </div>
            <p style={{ fontSize: 12, color: "var(--kr-fg-muted)", margin: "14px 0 0", lineHeight: 1.5 }}>
              El equipo asignado y los activos relacionados podrán gestionarse desde el detalle del ticket.
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
      <span style={{ color: "var(--kr-fg-muted)" }}>{label}</span>
      <span style={{ color: "var(--kr-fg-strong)", fontWeight: 500, textAlign: "right" }}>{value}</span>
    </div>
  );
}

export default SoporteCrear;
